import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import tokenService from "../services/tokenservice";

const { REACT_APP_URL_EASY_GESTAO } = process.env;

/* =====================================================
   TIPOS
===================================================== */

type NavigateFn = (path: string) => void;

type Config = {
  ignoreToast?: boolean;
  toast?: boolean;
  toastMessage?: string;
  printLoad?: boolean;
  xs?: string;
  [key: string]: any;
};

type ExecServiceParams = {
  url: string;
  data: any;
  config?: Config;
  type?: AxiosRequestConfig["responseType"];
};

/* =====================================================
   CONSTANTES
===================================================== */

const API_BASE_URL = REACT_APP_URL_EASY_GESTAO ?? "http://localhost:8787";

const toastSuccessConfig = { autoClose: 2000 };
const toastErrorConfig = { autoClose: 5000 };

/* =====================================================
   NAVEGAÇÃO EXTERNA
===================================================== */

let navigate: NavigateFn | null = null;

/* =====================================================
   AXIOS INSTANCE
===================================================== */

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/* =====================================================
   FUNÇÕES AUXILIARES
===================================================== */

async function buildHeaders(config?: Config): Promise<Record<string, string>> {
  const token = await tokenService.getToken();

  return {
    Authorization: `Bearer ${token}`,
    "x-s": config?.xs ?? (await tokenService.getEmpresaID()),
  };
}

function handleUnauthorized(error: any) {
  const status = error?.response?.status;

  if (status === 401) {
    tokenService.deleteToken();
    if (navigate) {
      navigate("/login");
    }
  }
}

function extractErrorMessage(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

/* =====================================================
   SERVICE API
===================================================== */

const ServiceAPI = {
  /* ---------------------------------------------
     Navegação externa (Router)
  --------------------------------------------- */
  setNavigate: (nav: NavigateFn) => {
    navigate = nav;
  },

  /* ---------------------------------------------
     Wrapper de Promise com Toast
  --------------------------------------------- */
  consumePromise: (promise: Promise<AxiosResponse>, config: Config = {}) => {
    if (!config.toast) {
      return ServiceAPI.buildPromise(promise);
    }

    return toast.promise(ServiceAPI.buildPromise(promise), {
      pending: config.toastMessage || "Comunicando com o servidor...",
    });
  },

  buildPromise: (promise: Promise<AxiosResponse>) => {
    return new Promise<any>((resolve, reject) => {
      promise
        .then((response) => resolve(response.data))
        .catch((error) => {
          handleUnauthorized(error);
          reject(error);
        });
    });
  },

  /* ---------------------------------------------
     Execução base de serviços
  --------------------------------------------- */
  execNativeService: async ({ url, data, config, type }: ExecServiceParams) => {
    const headers = await buildHeaders(config);

    const request = api.post(url, data, {
      headers,
      responseType: type,
    });

    return ServiceAPI.consumePromise(request, config);
  },

  consumeService: async (params: ExecServiceParams) => {
    return ServiceAPI.execNativeService(params);
  },

  /* ---------------------------------------------
     Autenticação
  --------------------------------------------- */
  logar: async (xs: string, data: any) => {
    return ServiceAPI.execNativeService({
      url: "/aut/login",
      data,
      config: { xs, printLoad: true },
    });
  },

  sair: async () => {
    await ServiceAPI.execNativeService({
      url: "/public/logout",
      data: null,
      config: {},
    });

    tokenService.deleteToken();

    if (navigate) {
      navigate(`/login/?${sessionStorage.getItem("X-S")}`);
    }
  },

  /* ---------------------------------------------
     CRUD / FIND
  --------------------------------------------- */
  createOrReplace: async <T = any>(config: any): Promise<T> => {
    try {
      const result = await ServiceAPI.execNativeService({
        url: "/crud/createreplace",
        data: config.data,
        config: config.config ?? { printLoad: true },
      });

      if (!config.config?.ignoreToast) {
        toast.success("Dados persistidos com sucesso.", toastSuccessConfig);
      }

      return result as T;
    } catch (error: any) {
      if (!config.config?.ignoreToast) {
        toast.error(
          extractErrorMessage(error, "Erro ao persistir dados."),
          toastErrorConfig
        );
      }
      throw error;
    }
  },

  findByPk: async <T = any>(config: any): Promise<T> => {
    try {
      return await ServiceAPI.execNativeService({
        url: "/find/filtrarbypk",
        data: config.data,
        config: {
          printLoad: config.config?.printLoad,
          ignoreToast: config.config?.ignoreToast,
        },
      });
    } catch (error: any) {
      toast.error(extractErrorMessage(error, "Erro ao buscar dados."));
      throw error;
    }
  },

  find: async <T = any>(data: any, printLoad = true): Promise<T> => {
    try {
      return await ServiceAPI.execNativeService({
        url: "/find/filtrar",
        data,
        config: { printLoad, ignoreToast: !printLoad },
      });
    } catch (error: any) {
      toast.error(extractErrorMessage(error, "Erro ao buscar dados."));
      throw error;
    }
  },

  delete: async <T = any>(config: any): Promise<T> => {
    try {
      const result = await ServiceAPI.execNativeService({
        url: config.config?.url ?? "/crud/delete",
        data: config.data,
        config: config.config?.config ?? { printLoad: true },
      });

      if (!config.config?.ignoreToast) {
        toast.success("Dados removidos com sucesso.", toastSuccessConfig);
      }

      return result as T;
    } catch (error: any) {
      toast.error(
        extractErrorMessage(error, "Erro ao remover dados."),
        toastErrorConfig
      );
      throw error;
    }
  },

  renovarToken: async () => {
  return ServiceAPI.execNativeService({
    url: "/aut/renovartoken",
    data: null,
    config: { ignoreToast: true },
  });
},
};

export default ServiceAPI;
